import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/Card.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';
import { MonthPanel } from './MonthPanel.js';
import { TodayPanel } from './TodayPanel.js';
import type { MonthSummary, TodaySummary } from './types.js';

/**
 * 355: the first screen staff open each morning (design 2a). Today, what needs action, down the left; This month, the
 * money and fleet picture, on the right; stacked on a phone, Today first.
 *
 * The two halves are separate reads (FR-022): one failing never blanks the other. Each re-reads every minute while
 * the page is visible and when the tab comes back (FR-023). A refusal and a failure are different facts and never
 * share a sentence, and neither shows zeros (062 FR-010).
 */
export const DASHBOARD_REFRESH_MS = 60_000;

const statusOf = (error: unknown) => (error as { status?: number } | null)?.status;
const isRefusal = (error: unknown) => statusOf(error) === 401 || statusOf(error) === 403;

function useHalf<T>(half: 'today' | 'month') {
  return useQuery({
    queryKey: ['dashboard', half],
    queryFn: () => api.get<T>(`/dashboard/${half}`),
    // A refusal is not a transient fault; a failure is offered a manual retry instead.
    retry: false,
    refetchInterval: DASHBOARD_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

function HalfError({ id, what, onRetry }: { id: string; what: string; onRetry: () => void }) {
  return (
    <Card data-testid={id}>
      <p className="text-sm text-steel">{what} could not be loaded. Try again shortly.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-lg border border-hairline-strong px-3 py-1.5 text-sm font-medium text-charcoal hover:bg-surface"
      >
        Try again
      </button>
    </Card>
  );
}

function Loading() {
  return (
    <div className="flex justify-center py-16">
      <Spinner size="lg" />
    </div>
  );
}

export function DashboardPage() {
  const queryClient = useQueryClient();
  const today = useHalf<TodaySummary>('today');
  const month = useHalf<MonthSummary>('month');

  const refused = isRefusal(today.error) && isRefusal(month.error);
  const updatedAt = Math.max(today.dataUpdatedAt, month.dataUpdatedAt);
  const asOf = today.data?.as_of ?? (updatedAt ? new Date(updatedAt).toISOString() : null);
  const supplier = (today.data?.scope ?? month.data?.scope) === 'supplier';

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description={
          asOf ? (
            <span data-testid="dashboard-day">
              {mytDayLabel(asOf)} · Malaysia time{supplier ? ' · your organisation’s cars' : ''}
            </span>
          ) : undefined
        }
        actions={
          refused ? undefined : (
            <>
              {updatedAt ? (
                <span
                  data-testid="dashboard-updated"
                  className="hidden text-sm text-steel sm:inline"
                >
                  Updated {mytClock(new Date(updatedAt))}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['dashboard'] })}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-hairline-strong px-3.5 text-sm font-medium text-charcoal hover:bg-surface"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Refresh
              </button>
            </>
          )
        }
      />

      {refused ? (
        <Card>
          <p className="text-sm text-steel">
            Platform statistics are not available for your account. Use the sections in the sidebar
            to manage your own fleet, pricing and bookings.
          </p>
        </Card>
      ) : (
        <div
          data-testid="dashboard-grid"
          className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[340px_minmax(0,1fr)]"
        >
          <section data-testid="dashboard-today" aria-label="Today">
            {today.data ? (
              <TodayPanel data={today.data} />
            ) : today.isError ? (
              <HalfError id="today-error" what="Today" onRetry={() => today.refetch()} />
            ) : (
              <Loading />
            )}
          </section>
          <section data-testid="dashboard-month" aria-label="This month" className="min-w-0">
            {month.data ? (
              <MonthPanel data={month.data} today={today.data?.day ?? month.data.month} />
            ) : month.isError ? (
              <HalfError id="month-error" what="This month" onRetry={() => month.refetch()} />
            ) : (
              <Loading />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
